
import type { LText } from '../types/scenario.ts';

export type Language = 'ca' | 'es' | 'en' | 'zh' | 'ar' | 'ja';

export const LANGUAGES: { code: Language; label: string; flag: string }[] = [
  { code: 'ca', label: 'Català', flag: '🏴󠁥󠁳󠁣󠁴󠁿' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'zh', label: '简体中文', flag: '🇨🇳' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
];

export type TranslationKey = keyof typeof translations.en;

const translations = {
  ca: {
    // General
    'app.title': 'Globdrop',
    'app.subtitle': 'Tria un repte i demostra la teva cultura geogràfica',
    'app.error.loading': "No s'ha pogut carregar els escenaris. Recarrega la pàgina.",
    
    // Scenario Selection
    'scenario.places': 'llocs',
    'scenario.points': 'punts configurats',
    'scenario.random.title': 'Sorprèn-me!',
    'scenario.random.desc': 'Juga a un repte a l\'atzar',
    'scenario.more_coming': 'Més reptes aviat...',
    'scenario.more_coming_desc': 'Estem treballant en nous reptes per a tu!',
    
    // Mode Selection
    'mode.back': 'Escollir un altre repte',
    
    // Game HUD
    'game.points': 'Punts',
    'game.timeout': 'Has estat massa lent!',
    'game.distance_to': 'fins a',
    'game.next': 'Següent',
    'game.round': 'de',
    
    // Results
    'results.title': 'Punts',
    'results.level_prefix': 'Tot un/a',
    'results.km_suffix': 'km innecessaris 😅',
    'results.mi_suffix': 'milles innecessàries 😅',
    'results.share': 'Comparteix i desafia!',
    'results.share_ig': "Missatge copiat! Enganxa'l a Instagram 📸",
    'results.share_copy': 'Copiat al porta-retalls! ✅',
    'footer.about': 'Sobre el joc',
    'results.copy': 'Copia el missatge',
    'game.map_alt': 'Mapa: ',
    'flags.flag_alt': 'Bandera per endevinar',
    'flags.choose_difficulty': 'Tria la dificultat:',
    'flags.choose_mode': 'Tria la modalitat de joc:',
    'results.share_wa': 'Obrint WhatsApp...',
    'results.share_fb': 'Obrint Facebook...',
    'results.replay': 'Repetir',
    'results.home': 'Inici',
    
    // Category Selector
    'category.maps.desc': 'Troba ciutats, cims i monuments al mapa interactiu.',
    'category.flags.desc': 'Posa a prova la teva cultura amb banderes del món, de regions i organitzacions.',
    'category.select_hint': 'Tria una de les dues modalitats per començar!',

    // Flags Game specific
    'tabs.maps': 'Mapes',
    'tabs.flags': 'Banderes',
    'flags.difficulty.easy': 'Fàcil',
    'flags.difficulty.medium': 'Mitjà',
    'flags.difficulty.hard': 'Difícil',
    'flags.mode.test': 'Tipo Test',
    'flags.mode.write': 'Escriure Nom',
    'flags.placeholder': 'Escriu el nom del país o regió...',
    'flags.submit': 'Envia',
    'flags.feedback.correct': 'Molt bé! Resposta correcta',
    'flags.feedback.incorrect': 'Incorrecte. Era ',
    'flags.feedback.timeout': 'Massa tard!',
    'flags.autocomplete.no_results': 'Sense resultats',
    'flags.results.level.novice': 'Novell/a',
    'flags.results.level.amateur': 'Aficionat/da',
    'flags.results.level.expert': 'Expert/a',
    'flags.results.level.master': 'Mestre/a de les Banderes',
  },
  es: {
    // General
    'app.title': 'Globdrop',
    'app.subtitle': 'Elige un desafío y demuestra tu cultura geográfica',
    'app.error.loading': 'No se han podido cargar los escenarios. Recarga la página.',
    
    // Scenario Selection
    'scenario.places': 'lugares',
    'scenario.points': 'puntos configurados',
    'scenario.random.title': '¡Sorpréndeme!',
    'scenario.random.desc': 'Juega a un desafío aleatorio',
    'scenario.more_coming': 'Más desafíos pronto...',
    'scenario.more_coming_desc': '¡Estamos trabajando en nuevos desafíos para ti!',
    
    // Mode Selection
    'mode.back': 'Elegir otro desafío',
    
    // Game HUD
    'game.points': 'Puntos',
    'game.timeout': '¡Has sido demasiado lento!',
    'game.distance_to': 'hasta',
    'game.next': 'Siguiente',
    'game.round': 'de',
    
    // Results
    'results.title': 'Puntos',
    'results.level_prefix': 'Eres un/a',
    'results.km_suffix': 'km innecesarios 😅',
    'results.mi_suffix': 'millas innecesarias 😅',
    'results.share': '¡Comparte y desafía!',
    'results.share_ig': "¡Mensaje copiado! Pégalo en Instagram 📸",
    'results.share_copy': '¡Copiado al portapapeles! ✅',
    'footer.about': 'Sobre el juego',
    'results.copy': 'Copiar el mensaje',
    'game.map_alt': 'Mapa: ',
    'flags.flag_alt': 'Bandera para adivinar',
    'flags.choose_difficulty': 'Elige la dificultad:',
    'flags.choose_mode': 'Elige la modalidad de juego:',
    'results.share_wa': 'Abriendo WhatsApp...',
    'results.share_fb': 'Abriendo Facebook...',
    'results.replay': 'Repetir',
    'results.home': 'Inicio',
    
    // Category Selector
    'category.maps.desc': 'Encuentra ciudades, cimas y monumentos en el mapa interactivo.',
    'category.flags.desc': 'Pon a prueba tu cultura con banderas del mundo, de regiones y organizaciones.',
    'category.select_hint': '¡Elige una de las dos modalidades para empezar!',

    // Flags Game specific
    'tabs.maps': 'Mapas',
    'tabs.flags': 'Banderas',
    'flags.difficulty.easy': 'Fácil',
    'flags.difficulty.medium': 'Medio',
    'flags.difficulty.hard': 'Difícil',
    'flags.mode.test': 'Tipo Test',
    'flags.mode.write': 'Escribir Nombre',
    'flags.placeholder': 'Escribe el nombre del país o región...',
    'flags.submit': 'Enviar',
    'flags.feedback.correct': '¡Muy bien! Respuesta correcta',
    'flags.feedback.incorrect': 'Incorrecto. Era ',
    'flags.feedback.timeout': '¡Demasiado tarde!',
    'flags.autocomplete.no_results': 'Sin resultados',
    'flags.results.level.novice': 'Novato/a',
    'flags.results.level.amateur': 'Aficionado/a',
    'flags.results.level.expert': 'Experto/a',
    'flags.results.level.master': 'Maestro/a de las Banderas',
  },
  en: {
    // General
    'app.title': 'Globdrop',
    'app.subtitle': 'Choose a challenge and prove your geographical knowledge',
    'app.error.loading': 'Failed to load scenarios. Please reload the page.',
    
    // Scenario Selection
    'scenario.places': 'places',
    'scenario.points': 'points configured',
    'scenario.random.title': 'I\'m feeling lucky!',
    'scenario.random.desc': 'Play a random challenge',
    'scenario.more_coming': 'More challenges soon...',
    'scenario.more_coming_desc': 'We are working on new challenges for you!',
    
    // Mode Selection
    'mode.back': 'Choose another challenge',
    
    // Game HUD
    'game.points': 'Points',
    'game.timeout': "You were too slow!",
    'game.distance_to': 'to',
    'game.next': 'Next',
    'game.round': 'of',
    
    // Results
    'results.title': 'Points',
    'results.level_prefix': 'A true',
    'results.km_suffix': 'unnecessary km 😅',
    'results.mi_suffix': 'unnecessary miles 😅',
    'results.share': 'Share and challenge!',
    'results.share_ig': "Message copied! Paste it on Instagram 📸",
    'results.share_copy': 'Copied to clipboard! ✅',
    'footer.about': 'About the game',
    'results.copy': 'Copy the message',
    'game.map_alt': 'Map: ',
    'flags.flag_alt': 'Flag to guess',
    'flags.choose_difficulty': 'Choose the difficulty:',
    'flags.choose_mode': 'Choose the game mode:',
    'results.share_wa': 'Opening WhatsApp...',
    'results.share_fb': 'Opening Facebook...',
    'results.replay': 'Replay',
    'results.home': 'Home',
    
    // Category Selector
    'category.maps.desc': 'Find cities, peaks, and monuments on the interactive map.',
    'category.flags.desc': 'Test your knowledge with world, regional, and organizational flags.',
    'category.select_hint': 'Choose one of the two modes to get started!',

    // Flags Game specific
    'tabs.maps': 'Maps',
    'tabs.flags': 'Flags',
    'flags.difficulty.easy': 'Easy',
    'flags.difficulty.medium': 'Medium',
    'flags.difficulty.hard': 'Hard',
    'flags.mode.test': 'Multiple Choice',
    'flags.mode.write': 'Write Name',
    'flags.placeholder': 'Type the country or region name...',
    'flags.submit': 'Submit',
    'flags.feedback.correct': 'Correct! Well done',
    'flags.feedback.incorrect': 'Incorrect. It was ',
    'flags.feedback.timeout': "Time's up!",
    'flags.autocomplete.no_results': 'No results found',
    'flags.results.level.novice': 'Novice',
    'flags.results.level.amateur': 'Amateur',
    'flags.results.level.expert': 'Expert',
    'flags.results.level.master': 'Flag Master',
  },
  zh: {
    // General
    'app.title': 'Globdrop',
    'app.subtitle': '选择挑战并证明你的地理知识',
    'app.error.loading': '无法加载场景。请刷新页面。',
    
    // Scenario Selection
    'scenario.places': '地点',
    'scenario.points': '点已配置',
    'scenario.random.title': '试试手气！',
    'scenario.random.desc': '玩一个随机挑战',
    'scenario.more_coming': '更多挑战即将推出...',
    'scenario.more_coming_desc': '我们正在为你制作新挑战！',
    
    // Mode Selection
    'mode.back': '选择另一个挑战',
    
    // Game HUD
    'game.points': '分数',
    'game.timeout': '你太慢了！',
    'game.distance_to': '到',
    'game.next': '下一步',
    'game.round': '/',
    
    // Results
    'results.title': '分数',
    'results.level_prefix': '一个真正的',
    'results.km_suffix': '公里多余 😅',
    'results.mi_suffix': '英里多余 😅',
    'results.share': '分享并挑战！',
    'results.share_ig': '消息已复制！粘贴到 Instagram 📸',
    'results.share_copy': '已复制到剪贴板！ ✅',
    'footer.about': '关于游戏',
    'results.copy': '复制消息',
    'game.map_alt': '地图：',
    'flags.flag_alt': '要猜的旗帜',
    'flags.choose_difficulty': '选择难度：',
    'flags.choose_mode': '选择游戏模式：',
    'results.share_wa': '正在打开 WhatsApp...',
    'results.share_fb': '正在打开 Facebook...',
    'results.replay': '重玩',
    'results.home': '主页',
    
    // Category Selector
    'category.maps.desc': '在互动地图上寻找城市、山峰和纪念碑。',
    'category.flags.desc': '用世界、地区和组织的旗帜测试你的知识。',
    'category.select_hint': '选择两种模式之一开始！',

    // Flags Game specific
    'tabs.maps': '地图',
    'tabs.flags': '国旗',
    'flags.difficulty.easy': '简单',
    'flags.difficulty.medium': '中等',
    'flags.difficulty.hard': '困难',
    'flags.mode.test': '多项选择',
    'flags.mode.write': '拼写国名',
    'flags.placeholder': '输入国家或地区名称...',
    'flags.submit': '提交',
    'flags.feedback.correct': '完全正确！',
    'flags.feedback.incorrect': '错误。正确答案是 ',
    'flags.feedback.timeout': '时间到！',
    'flags.autocomplete.no_results': '未找到结果',
    'flags.results.level.novice': '国旗新手',
    'flags.results.level.amateur': '国旗爱好者',
    'flags.results.level.expert': '国旗专家',
    'flags.results.level.master': '国旗大师',
  },
  ar: {
    // General
    'app.title': 'Globdrop',
    'app.subtitle': 'اختر تحدياً وأثبت معرفتك الجغرافية',
    'app.error.loading': 'فشل تحميل السيناريوهات. يرجى إعادة تحميل الصفحة.',
    
    // Scenario Selection
    'scenario.places': 'أماكن',
    'scenario.points': 'نقاط محددة',
    'scenario.random.title': 'ضربة حظ!',
    'scenario.random.desc': 'العب تحدياً عشوائياً',
    'scenario.more_coming': 'المزيد من التحديات قريباً...',
    'scenario.more_coming_desc': 'نعمل على تحديات جديدة من أجلك!',
    
    // Mode Selection
    'mode.back': 'اختر تحدياً آخر',
    
    // Game HUD
    'game.points': 'نقاط',
    'game.timeout': 'لقد كنت بطيئًا جدًا!',
    'game.distance_to': 'إلى',
    'game.next': 'التالي',
    'game.round': 'من',
    
    // Results
    'results.title': 'نقاط',
    'results.level_prefix': 'أنت',
    'results.km_suffix': 'كم غير ضرورية 😅',
    'results.mi_suffix': 'ميل غير ضروري 😅',
    'results.share': 'شارك وتحدَّ!',
    'results.share_ig': 'تم نسخ الرسالة! الصقها في Instagram 📸',
    'results.share_copy': 'تم النسخ إلى الحافظة! ✅',
    'footer.about': 'عن اللعبة',
    'results.copy': 'انسخ الرسالة',
    'game.map_alt': 'خريطة: ',
    'flags.flag_alt': 'العلم المطلوب تخمينه',
    'flags.choose_difficulty': 'اختر مستوى الصعوبة:',
    'flags.choose_mode': 'اختر طريقة اللعب:',
    'results.share_wa': 'جاري فتح WhatsApp...',
    'results.share_fb': 'جاري فتح Facebook...',
    'results.replay': 'إعادة اللعب',
    'results.home': 'الرئيسية',
    
    // Category Selector
    'category.maps.desc': 'ابحث عن المدن والقمم والمعالم على الخريطة التفاعلية.',
    'category.flags.desc': 'اختبر معرفتك بأعلام العالم والأقاليم والمنظمات.',
    'category.select_hint': 'اختر أحد الوضعين للبدء!',

    // Flags Game specific
    'tabs.maps': 'خرائط',
    'tabs.flags': 'أعلام',
    'flags.difficulty.easy': 'سهل',
    'flags.difficulty.medium': 'متوسط',
    'flags.difficulty.hard': 'صعب',
    'flags.mode.test': 'خيارات متعددة',
    'flags.mode.write': 'كتابة الاسم',
    'flags.placeholder': 'اكتب اسم الدولة أو المنطقة...',
    'flags.submit': 'إرسال',
    'flags.feedback.correct': 'ممتاز! إجابة صحيحة',
    'flags.feedback.incorrect': 'خطأ. كانت ',
    'flags.feedback.timeout': 'انتهى الوقت!',
    'flags.autocomplete.no_results': 'لم يتم العثور على نتائج',
    'flags.results.level.novice': 'مبتدئ أعلام',
    'flags.results.level.amateur': 'هاوي أعلام',
    'flags.results.level.expert': 'خبير أعلام',
    'flags.results.level.master': 'سيد الأعلام',
  },
  ja: {
    // General
    'app.title': 'Globdrop',
    'app.subtitle': 'チャレンジを選択して地理知識を証明しましょう',
    'app.error.loading': 'シナリオの読み込みに失敗しました。ページを更新してください。',
    
    // Scenario Selection
    'scenario.places': '地点',
    'scenario.points': '設定済みポイント',
    'scenario.random.title': 'I\'m feeling lucky!',
    'scenario.random.desc': 'ランダムなチャレンジをプレイする',
    'scenario.more_coming': '新しいチャレンジが近日公開されます...',
    'scenario.more_coming_desc': '新しいチャレンジを制作中です！',
    
    // Mode Selection
    'mode.back': '別のチャレンジを選択',
    
    // Game HUD
    'game.points': 'ポイント',
    'game.timeout': '遅すぎます！',
    'game.distance_to': 'まで',
    'game.next': '次へ',
    'game.round': '/',
    
    // Results
    'results.title': 'ポイント',
    'results.level_prefix': 'あなたは真の',
    'results.km_suffix': 'km 余計でした 😅',
    'results.mi_suffix': 'マイル余計でした 😅',
    'results.share': 'シェアして挑戦！',
    'results.share_ig': 'メッセージをコピーしました！Instagramに貼り付けてください 📸',
    'results.share_copy': 'クリップボードにコピーしました！ ✅',
    'footer.about': 'このゲームについて',
    'results.copy': 'メッセージをコピー',
    'game.map_alt': '地図：',
    'flags.flag_alt': '当てる国旗',
    'flags.choose_difficulty': '難易度を選んでください：',
    'flags.choose_mode': 'ゲームモードを選んでください：',
    'results.share_wa': 'WhatsAppを開いています...',
    'results.share_fb': 'Facebookを開いています...',
    'results.replay': 'もう一度',
    'results.home': 'ホーム',
    
    // Category Selector
    'category.maps.desc': 'インタラクティブマップで都市、山頂、記念碑を見つけましょう。',
    'category.flags.desc': '世界、地域、組織の旗で知識をテストしましょう。',
    'category.select_hint': '2つのモードのいずれかを選択して開始します！',

    // Flags Game specific
    'tabs.maps': 'マップ',
    'tabs.flags': '国旗',
    'flags.difficulty.easy': '簡単',
    'flags.difficulty.medium': '普通',
    'flags.difficulty.hard': '難しい',
    'flags.mode.test': '選択肢クイズ',
    'flags.mode.write': '名前入力',
    'flags.placeholder': '国や地域の名前を入力...',
    'flags.submit': '送信',
    'flags.feedback.correct': '正解！素晴らしい',
    'flags.feedback.incorrect': '不正解。正解は ',
    'flags.feedback.timeout': '時間切れ！',
    'flags.autocomplete.no_results': '結果が見つかりません',
    'flags.results.level.novice': '国旗ビギナー',
    'flags.results.level.amateur': '国旗アマチュア',
    'flags.results.level.expert': '国旗エキスパート',
    'flags.results.level.master': '国旗マスター',
  },
};

function getStoredLang(): Language | null {
  try {
    return (typeof localStorage !== 'undefined' && localStorage && typeof localStorage.getItem === 'function' ? localStorage.getItem('lang') : null) as Language | null;
  } catch {
    return null;
  }
}

let currentLang: Language = getStoredLang() || 'ca';

const LOCALES: Record<Language, string> = {
  ca: 'ca-ES',
  es: 'es-ES',
  en: 'en-US',
  zh: 'zh-CN',
  ar: 'ar',
  ja: 'ja-JP',
};

/** Locale for toLocaleString, so numbers follow the chosen language */
export function getLocale(): string {
  return LOCALES[currentLang];
}

/** Set lang and dir on <html>: Arabic is laid out right to left */
function applyDocumentLanguage(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = currentLang;
  document.documentElement.dir = currentLang === 'ar' ? 'rtl' : 'ltr';
}

applyDocumentLanguage();

export function getLanguage(): Language {
  return currentLang;
}

export function setLanguage(lang: Language): void {
  currentLang = lang;
  applyDocumentLanguage();
  try {
    if (typeof localStorage !== 'undefined' && localStorage && typeof localStorage.setItem === 'function') {
      localStorage.setItem('lang', lang);
    }
  } catch {
    // Ignore
  }
}

export function t(key: TranslationKey): string {
  return translations[currentLang][key] || translations['en'][key] || key;
}

/**
 * For objects that have localized fields like { ca: "...", es: "...", en: "..." }
 * or if they are just strings, falls back to the string itself.
 * If the current language is missing, falls back to English.
 */
export function getLocalized(obj: LText | undefined | null): string {
  if (obj == null) return '';
  if (typeof obj === 'string') return obj;
  if (obj[currentLang]) return obj[currentLang]!;
  if (obj['en']) return obj['en']!;
  // Fallback to first available translation
  const keys = Object.keys(obj);
  return keys.length > 0 ? obj[keys[0]!]! : '';
}
